import { Button } from '@material-ui/core';
import { useCameraViewContext } from '../../context';
import { useCallback } from 'react';
import { useField, useForm } from 'react-final-form';
import styled from 'styled-components';
import { useFieldArray } from 'react-final-form-arrays';
import { SyncManager } from 'db';
import { useVideoUpload } from '../../hooks/useVideoUpload';
import { SaveImageFn, useUpdateImage } from 'pages/ListerVehicleUploadV2/hooks/useUpdateImage';
import { useFormVehicleId } from 'pages/ListerVehicleUploadV2/hooks/useFormVehicleId';
import { UploadStatus, useVideoUploadStatus } from 'services/listerUploadVehicle/v2';
import toast from 'react-hot-toast';
import { useNonBlockingImageUpload } from 'pages/ListerVehicleUploadV2/hooks/useNonBlockingImageUpload';
import { useIsOnline } from 'hooks';
import { useInfoToast } from 'components/InfoToast/useInfoToast';
import { parseFile } from 'utils/file';
import { resizeV2 } from 'utils/image';
import { dataURLtoFile } from 'utils';
import { EXTERIOR_CATEGORIES, getExteriorCategoryFromIndex, isRightBoundaryIndex } from '../../constants/imageCategory';
import { getIn } from 'final-form';
import { useNonBlockingImageUpload2 } from 'pages/ListerVehicleUploadV2/hooks/useNonBlockingImageUpload2';
import { useUpdateImage2 } from 'pages/ListerVehicleUploadV2/hooks/useUpdateImage2';
import { SectionImage } from 'services/api/vehicle-sections/types';

const Wrapper = styled.div`
  padding: 0px 16px;
`;

const StyledButton = styled(Button)`
  border-radius: 6px;
  text-transform: none;
  width: 100%;
  height: 44px;

  &:disabled {
    background-color: #005a96;
    color: #ffffff;
    opacity: 0.5;

    &:hover {
      background-color: #005a96;
      color: #ffffff;
      opacity: 0.5;
    }
  }

  &:hover {
    background-color: #005a96;
    color: #ffffff;
  }
`;

type TakeNextOptions = {
  switchToCameraView?: boolean;
};

export const useTakeNextHandlerLegacy = ({ switchToCameraView = true }: TakeNextOptions = {}) => {
  const vehicleId = useFormVehicleId();

  const fireInfoToast = useInfoToast();

  const form = useForm();

  const formatSyncPayload = ({ skipFile = false }) => {
    const payload: Parameters<SaveImageFn>[0] = {};

    if (!skipFile) {
      payload.file = currentImageSrc;
    }
    payload.is_damaged = !!isDamaged;
    if (grade) {
      payload.grade = grade;
    }
    if (notes?.message) {
      payload.notes = notes?.message;
    }
    if (expirationDate) {
      payload.expiration_date = expirationDate;
    }

    return payload;
  };

  const {
    currentImageSrc,
    level1SectionId,
    imageIndex,
    changeIndex,
    setIndex,
    isVideoMode,
    recordedChunks,
    isRecording,
    setImageLoading,
    imageLoading,
    type,
    closeCameraView
  } = useCameraViewContext();

  const nonBlockingUpload = useNonBlockingImageUpload(level1SectionId!, imageIndex);
  const { fields: images } = useFieldArray(`${level1SectionId}.images`);

  const updateImage = useUpdateImage(level1SectionId!, imageIndex);

  const uploadVideo = useVideoUpload(level1SectionId!);

  const { input: urlInput } = useField(`${level1SectionId}.images.${imageIndex}.url`);
  const {
    input: { value: expirationDate }
  } = useField(`${level1SectionId}.images.${imageIndex}.expiration_date`);
  const {
    input: { value: isDamaged }
  } = useField(`${level1SectionId}.images.${imageIndex}.is_damaged`);
  const {
    input: { value: notes }
  } = useField(`${level1SectionId}.images.${imageIndex}.notes`);
  const {
    input: { value: grade }
  } = useField(`${level1SectionId}.images.${imageIndex}.grade`);

  // const {
  //   input: { value: nextValue }
  // } = useField(`${level1SectionId}.images.${imageIndex + 1}.url`);

  const uploadStatus = useVideoUploadStatus(vehicleId);

  const isOnline = useIsOnline();

  const { input } = useField(`${level1SectionId}.images.${imageIndex}`);

  const handleInitialImageUpload = async (file: File) => {
    const parsedFile = await parseFile(file);
    const resized = await resizeV2(parsedFile, { width: 1600, height: 1200 }, 'jpeg', 0.8);
    input.onChange({
      ...input.value,
      file: resized,
      local_url: URL.createObjectURL(resized)
    });
  };

  return async () => {
    if (imageLoading) {
      return { loading: true };
    }

    if (type === 'initial_image') {
      await handleInitialImageUpload(dataURLtoFile(currentImageSrc));
      closeCameraView();
    }

    if (isVideoMode) {
      if (recordedChunks.length === 0 || isRecording) {
        toast.error('Nothing to upload');
        return;
      }

      if (uploadStatus === UploadStatus.IN_PROGRESS || uploadStatus === UploadStatus.QUEUED) {
        toast.error('Video upload is in progress');
        return;
      }

      setImageLoading(true);
      fireInfoToast('Uploading to the server...');
      await uploadVideo(recordedChunks);
      setImageLoading(false);
      return;
    }

    const switchToNextIndex = (() => {
      if (!switchToCameraView) return false;
      return !isRightBoundaryIndex(imageIndex);
    })();

    if (!currentImageSrc && urlInput.value) {
      const payload = formatSyncPayload({ skipFile: true });
      await updateImage(payload);
      if (!switchToNextIndex) {
        return;
      }
      return setIndex(images.length || 0);
    }

    if (isOnline) {
      console.log('non blocking upload');
      nonBlockingUpload(currentImageSrc!);
    } else {
      const payload = formatSyncPayload({ skipFile: false });
      await updateImage(payload);
    }

    if (switchToNextIndex) {
      changeIndex();
    }
  };
};

type FormatPayloadArgs = {
  currentImageSrc: string | null;
  image: SectionImage;
  skipFile?: boolean;
};

const formatSyncPayload = ({ skipFile = false, image, currentImageSrc }: FormatPayloadArgs) => {
  const payload: Parameters<SaveImageFn>[0] = {};

  if (!skipFile) {
    payload.file = currentImageSrc;
  }
  payload.is_damaged = !!image.is_damaged;
  if (image.grade) {
    payload.grade = image.grade;
  }
  if (image.notes?.message) {
    payload.notes = image.notes?.message;
  }
  if (image.expiration_date) {
    payload.expiration_date = image.expiration_date;
  }

  return payload;
};

export const useTakeNextHandler = ({ switchToCameraView = true }: TakeNextOptions = {}) => {
  const vehicleId = useFormVehicleId();

  const fireInfoToast = useInfoToast();

  const form = useForm();

  const {
    currentImageSrc,
    level1SectionId,
    imageIndex,
    changeIndex,
    setIndex,
    isVideoMode,
    recordedChunks,
    isRecording,
    setImageLoading,
    imageLoading,
    type,
    closeCameraView,
    openCameraView
  } = useCameraViewContext();

  const nonBlockingUpload = useNonBlockingImageUpload2();
  const saveImage = useUpdateImage2();

  const uploadVideo = useVideoUpload(level1SectionId!);

  const uploadStatus = useVideoUploadStatus(vehicleId);

  const isOnline = useIsOnline();

  const { input } = useField(`${level1SectionId}.images.${imageIndex}`);

  const handleInitialImageUpload = async (file: File) => {
    const parsedFile = await parseFile(file);
    const resized = await resizeV2(parsedFile, { width: 1600, height: 1200 }, 'jpeg', 0.8);
    input.onChange({
      ...input.value,
      file: resized,
      local_url: URL.createObjectURL(resized)
    });
  };

  return async () => {
    const formValues = form.getState().values;
    const hasNextUrl = !!getIn(formValues, `${level1SectionId}.images.${imageIndex + 1}.url`);
    const imageValue = getIn(formValues, `${level1SectionId}.images.${imageIndex}`);

    if (imageLoading) {
      return { loading: true };
    }

    if (type === 'initial_image') {
      await handleInitialImageUpload(dataURLtoFile(currentImageSrc));
      closeCameraView();
    }

    if (isVideoMode) {
      if (recordedChunks.length === 0 || isRecording) {
        toast.error('Nothing to upload');
        return;
      }

      if (uploadStatus === UploadStatus.IN_PROGRESS || uploadStatus === UploadStatus.QUEUED) {
        toast.error('Video upload is in progress');
        return;
      }

      setImageLoading(true);
      fireInfoToast('Uploading to the server...');
      await uploadVideo(recordedChunks);
      setImageLoading(false);
      return;
    }

    const afterTake = () => {
      if (!switchToCameraView) return;
      if (!currentImageSrc && imageValue.url) {
        return setIndex(imageIndex + 1, hasNextUrl);
      }
      return changeIndex(false);
    };

    if (!currentImageSrc && imageValue.url) {
      const payload = formatSyncPayload({ skipFile: true, image: imageValue, currentImageSrc });
      await saveImage(level1SectionId!, imageIndex, payload);
      return afterTake();
    }

    if (isOnline) {
      console.log('non blocking upload');
      nonBlockingUpload(level1SectionId!, imageIndex, currentImageSrc!);
    } else {
      const payload = formatSyncPayload({ skipFile: false, image: imageValue, currentImageSrc });
      await saveImage(level1SectionId!, imageIndex, payload);
    }

    afterTake();
  };
};

export const TakeNextButton = () => {
  const label = 'Next';
  const handleNext = useTakeNextHandler();
  const { level1SectionId, imageLoading, imageIndex } = useCameraViewContext();
  const {
    input: { value: url }
  } = useField(`${level1SectionId}.images.${imageIndex}.url`);
  const {
    input: { value: localUrl }
  } = useField(`${level1SectionId}.images.${imageIndex}.local_url`);

  if (level1SectionId === 'drive_cycles' || imageIndex === 49) {
    return null;
  }

  return (
    <Wrapper>
      <StyledButton disabled={imageLoading} color="primary" variant="contained" onClick={handleNext}>
        {label}
      </StyledButton>
    </Wrapper>
  );
};

export const SaveVideoButton = () => {
  const label = 'Save Video';
  const handleNext = useTakeNextHandler();
  const { level1SectionId, imageLoading, setIndex } = useCameraViewContext();

  const { fields } = useFieldArray(`${level1SectionId}.images`);

  const handleClick = async () => {
    await handleNext();
    setIndex(fields.length!);
  };

  return (
    <Wrapper>
      <StyledButton disabled={imageLoading} color="primary" variant="contained" onClick={handleClick}>
        {label}
      </StyledButton>
    </Wrapper>
  );
};
